import React, {
  forwardRef,
  useCallback,
  useEffect,
  useId,
  useRef,
  useState,
} from 'react';
import { InputGroup, AutoComplete } from 'rsuite';
import FormFieldErrorIcon from './FormFieldErrorIcon';
import IconIndicator from '../user-feedback/IconIndicator';
import { AddressOption } from 'src/types/address';

export interface AutocompleteWithErrorStatusProps {
  autoComplete?: string;
  data: Array<AddressOption>;
  disabled?: boolean;
  errorMessage: string;
  hideErrorIcon?: boolean;
  loading?: boolean;
  name?: string;
  onChange: (value: string) => void;
  onSelect?: (value?: AddressOption) => void;
  showError?: boolean;
  value?: AddressOption | string;
}

export const AutocompleteWithErrorStatus = React.memo(
  forwardRef<HTMLDivElement, AutocompleteWithErrorStatusProps>(
    (
      {
        errorMessage,
        hideErrorIcon,
        loading,
        showError,
        onSelect,
        value,
        ...autoCompleteProps
      },
      ref
    ) => {
      const inputId = useId();
      const listboxId = `${inputId}-listbox`;
      const [keepOpen, setKeepOpen] = useState(false);
      const userDismissedRef = useRef(false);
      const dataRef = useRef(autoCompleteProps.data);
      dataRef.current = autoCompleteProps.data;

      // Native blur fires before React's synthetic focusout reaches RSuite, so
      // setting keepOpen here schedules the re-render before RSuite can close.
      // Only null relatedTarget is treated as a screen reader synthetic blur —
      // real user actions (click, Tab) always produce a non-null relatedTarget
      // on the native blur event. This distinction lets handleContainerBlur in
      // CityRestrictedAddressLookup read the data-sr-keepopen attribute and skip
      // clearing addressOptions only during screen reader navigation, while still
      // clearing for genuine focus-away events (which have non-null relatedTarget
      // and never set keepOpen / data-sr-keepopen).
      useEffect(() => {
        const input = document.getElementById(inputId);
        if (!input) return;
        const handleBlur = (e: FocusEvent) => {
          if (userDismissedRef.current) {
            userDismissedRef.current = false;
            return;
          }
          if (!e.relatedTarget && dataRef.current.length > 0) {
            setKeepOpen(true);
          }
        };
        input.addEventListener('blur', handleBlur);
        return () => input.removeEventListener('blur', handleBlur);
      }, [inputId]);

      // Sync keepOpen to a DOM attribute so handleContainerBlur (on the parent
      // container div in CityRestrictedAddressLookup) can check it without
      // needing keepOpen passed through the component chain.
      useEffect(() => {
        const input = document.getElementById(inputId);
        if (!input) return;
        if (keepOpen) {
          input.setAttribute('data-sr-keepopen', 'true');
        } else {
          input.removeAttribute('data-sr-keepopen');
        }
      }, [keepOpen, inputId]);

      const handleOpen = useCallback(() => {
        userDismissedRef.current = false;
        requestAnimationFrame(() => {
          const listbox = document.getElementById(listboxId);
          if (listbox) {
            listbox.setAttribute(
              'aria-label',
              `${autoCompleteProps.name ?? ''} suggestions`
            );
            // tabindex="0" intentionally omitted — see scrollable-region-focusable
            // suppression in testUtils/axe.ts for rationale.
          }
        });
      }, [listboxId, autoCompleteProps.name]);

      const handleKeyDown = useCallback((e: React.KeyboardEvent<Element>) => {
        if (e.key === 'Escape' || e.key === 'Tab') {
          userDismissedRef.current = true;
          setKeepOpen(false);
        }
      }, []);

      useEffect(() => {
        if (autoCompleteProps.data.length === 0) {
          setKeepOpen(false);
        }
      }, [autoCompleteProps.data.length]);

      const errorId =
        showError && autoCompleteProps.name
          ? `${autoCompleteProps.name}-error`
          : undefined;

      return (
        <>
          <InputGroup
            role="group"
            ref={ref}
          >
            <AutoComplete
              {...autoCompleteProps}
              id={inputId}
              {...(keepOpen ? { open: true } : {})}
              aria-controls={listboxId}
              aria-label={autoCompleteProps.name}
              aria-invalid={showError || undefined}
              aria-describedby={errorId}
              autoComplete={autoCompleteProps.autoComplete ?? 'off'}
              onOpen={handleOpen}
              onKeyDown={handleKeyDown}
              onSelect={(selected) => {
                userDismissedRef.current = true;
                setKeepOpen(false);
                const match = autoCompleteProps.data.find(
                  (add) => add.value === selected
                );
                if (onSelect)
                  onSelect(match ?? ({ value: selected } as AddressOption));
              }}
              value={typeof value === 'object' ? value?.value : value}
            />
            {!hideErrorIcon && showError && (
              <InputGroup.Addon
                tabIndex={-1}
                data-testid="error-addon"
              >
                <FormFieldErrorIcon errorMessage={errorMessage} />
              </InputGroup.Addon>
            )}
            {loading && (
              <InputGroup.Addon
                tabIndex={-1}
                style={{ backgroundColor: 'transparent' }}
              >
                <IconIndicator
                  size={16}
                  color="#D1D1D1"
                />
              </InputGroup.Addon>
            )}
          </InputGroup>
          {showError && errorMessage && (
            <span
              id={`${autoCompleteProps.name}-error`}
              className="text-error"
            >
              {errorMessage}
            </span>
          )}
        </>
      );
    }
  )
);
